import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  ArcElement,
  PieController,
  Tooltip,
  Legend,
  type ChartConfiguration,
} from "chart.js";

ChartJS.register(ArcElement, PieController, Tooltip, Legend);

const DEFAULT_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6"];

interface Props {
  data: {
    type: "pie_chart";
    title?: string;
    labels: string[];
    values: number[];
    colors?: string[];
    animation?: string;
  };
}

export function ElementPieChart({ data }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !data.labels?.length) return;
    const colors = data.colors?.length ? data.colors : DEFAULT_COLORS;
    const config: ChartConfiguration<"pie"> = {
      type: "pie",
      data: {
        labels: data.labels,
        datasets: [
          {
            data: data.values,
            backgroundColor: data.labels.map((_, i) => colors[i % colors.length]),
            borderWidth: 1,
            borderColor: "#fff",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: !!data.title, text: data.title ?? "" },
          legend: { position: "bottom" },
        },
      },
    };
    const chart = new ChartJS(canvasRef.current, config);
    return () => chart.destroy();
  }, [data]);

  return (
    <motion.div
      className="h-48 w-full"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <canvas ref={canvasRef} />
    </motion.div>
  );
}
